import { useCallback, useEffect, useState } from "react";
import { Mail, Plus, RefreshCw, Trash2, Inbox, Send, Link2, Loader2, AlertTriangle, Settings2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import api from "../../lib/api";
import { useGearsVisible } from "../../lib/gearVisibility";

const fmt = (iso) => { try { return new Date(iso).toLocaleString("de-DE"); } catch { return iso || "—"; } };

function AddAccount({ onAdded }) {
    const gearsOk = useGearsVisible();

    const [open, setOpen] = useState(false);
    const [adv, setAdv] = useState(false);
    const [busy, setBusy] = useState(false);
    const [f, setF] = useState({ address: "", display_name: "", password: "", username: "", imap_host: "", imap_port: "", smtp_host: "", smtp_port: "" });
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
    const submit = async () => {
        if (!f.address.includes("@")) { toast.error("Bitte eine gültige E-Mail-Adresse angeben."); return; }
        setBusy(true);
        try {
            await api.post("/dealer/leasing/email-accounts", f);
            toast.success("Postfach hinzugefügt.");
            setF({ address: "", display_name: "", password: "", username: "", imap_host: "", imap_port: "", smtp_host: "", smtp_port: "" });
            setOpen(false); onAdded();
        } catch (e) { toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen."); }
        finally { setBusy(false); }
    };
    if (!open) return (
        <Button onClick={() => setOpen(true)} variant="outline" className="rounded-sm" data-testid="leasing-email-add-open"><Plus className="w-4 h-4 mr-2" /> Postfach hinzufügen</Button>
    );
    return (
        <div className="border rounded-sm p-4 bg-gray-50 space-y-3" data-testid="leasing-email-add-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="text-[10px] uppercase font-bold text-gray-500">E-Mail-Adresse</label><Input value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="wir-wollen-deine-vespa@autohaus-koenig.de" className="rounded-sm" data-testid="leasing-email-address" /></div>
                <div><label className="text-[10px] uppercase font-bold text-gray-500">Anzeigename (optional)</label><Input value={f.display_name} onChange={(e) => set("display_name", e.target.value)} className="rounded-sm" data-testid="leasing-email-displayname" /></div>
                <div><label className="text-[10px] uppercase font-bold text-gray-500">App-Passwort</label><Input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="App-Passwort (nicht dein Login)" className="rounded-sm" data-testid="leasing-email-password" /></div>
                <div><label className="text-[10px] uppercase font-bold text-gray-500">Benutzername (optional)</label><Input value={f.username} onChange={(e) => set("username", e.target.value)} placeholder="meist = E-Mail" className="rounded-sm" data-testid="leasing-email-username" /></div>
            </div>
            {gearsOk && <button onClick={() => setAdv((v) => !v)} className="text-xs font-bold text-indigo-600 inline-flex items-center gap-1" data-testid="leasing-email-adv-toggle"><Settings2 className="w-3.5 h-3.5" /> Server-Einstellungen {adv ? "ausblenden" : "anzeigen"}</button>}
            {adv && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div><label className="text-[10px] uppercase font-bold text-gray-500">IMAP-Server</label><Input value={f.imap_host} onChange={(e) => set("imap_host", e.target.value)} placeholder="mail.domain.de" className="rounded-sm" data-testid="leasing-email-imap-host" /></div>
                    <div><label className="text-[10px] uppercase font-bold text-gray-500">IMAP-Port</label><Input value={f.imap_port} onChange={(e) => set("imap_port", e.target.value)} placeholder="993" className="rounded-sm font-mono-num" /></div>
                    <div><label className="text-[10px] uppercase font-bold text-gray-500">SMTP-Server</label><Input value={f.smtp_host} onChange={(e) => set("smtp_host", e.target.value)} placeholder="mail.domain.de" className="rounded-sm" data-testid="leasing-email-smtp-host" /></div>
                    <div><label className="text-[10px] uppercase font-bold text-gray-500">SMTP-Port</label><Input value={f.smtp_port} onChange={(e) => set("smtp_port", e.target.value)} placeholder="587" className="rounded-sm font-mono-num" /></div>
                </div>
            )}
            <div className="flex justify-end gap-2">
                <Button onClick={() => setOpen(false)} variant="ghost" className="rounded-sm text-gray-500">Abbrechen</Button>
                <Button onClick={submit} disabled={busy} className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="leasing-email-add-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Hinzufügen</Button>
            </div>
        </div>
    );
}

function AccountRow({ acc, onChanged }) {
    const gearsOk = useGearsVisible();

    const [busy, setBusy] = useState(false);
    const [edit, setEdit] = useState(false);
    const [pwd, setPwd] = useState("");
    const sync = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/dealer/leasing/email-accounts/${acc.id}/sync`);
            toast.success(`Abruf: ${data.new} neu, ${data.matched} zugeordnet (von ${data.fetched}).`);
            onChanged();
        } catch (e) { toast.error(e?.response?.data?.detail || "Abruf fehlgeschlagen."); }
        finally { setBusy(false); }
    };
    const savePwd = async () => {
        try { await api.patch(`/dealer/leasing/email-accounts/${acc.id}`, { password: pwd }); toast.success("Zugang aktualisiert."); setPwd(""); setEdit(false); onChanged(); }
        catch { toast.error("Speichern fehlgeschlagen."); }
    };
    const del = async () => {
        if (!window.confirm("Postfach inkl. abgerufener E-Mails entfernen?")) return;
        try { await api.delete(`/dealer/leasing/email-accounts/${acc.id}`); toast.success("Postfach entfernt."); onChanged(); }
        catch { toast.error("Löschen fehlgeschlagen."); }
    };
    return (
        <div className="border rounded-sm p-3 bg-white" data-testid={`leasing-email-acct-${acc.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 font-bold text-sm"><Mail className="w-4 h-4 text-indigo-600" /> {acc.address}
                        {acc.has_credentials
                            ? <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800">verbunden</span>
                            : <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800">Zugang fehlt</span>}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">IMAP {acc.imap_host}:{acc.imap_port} · Letzter Abruf {acc.last_sync_at ? fmt(acc.last_sync_at) : "—"}</div>
                    {acc.last_sync_error && <div className="text-[11px] text-red-600 mt-0.5 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {acc.last_sync_error}</div>}
                </div>
                <div className="flex gap-2">
                    <Button onClick={sync} disabled={busy} variant="outline" className="rounded-sm h-8 text-xs" data-testid={`leasing-email-sync-${acc.id}`}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Abrufen</Button>
                    {gearsOk && <Button onClick={() => setEdit((v) => !v)} variant="ghost" className="rounded-sm h-8 text-xs text-gray-600" data-testid={`leasing-email-editpw-${acc.id}`}><Settings2 className="w-3.5 h-3.5 mr-1" /> Zugang</Button>}
                    <Button onClick={del} variant="ghost" className="rounded-sm h-8 text-xs text-red-500" data-testid={`leasing-email-del-${acc.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
            </div>
            {edit && (
                <div className="flex items-end gap-2 mt-2 pt-2 border-t">
                    <div className="flex-1"><label className="text-[10px] uppercase font-bold text-gray-500">Neues App-Passwort</label><Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="rounded-sm" data-testid={`leasing-email-pw-input-${acc.id}`} /></div>
                    <Button onClick={savePwd} disabled={!pwd} className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-white h-9 text-xs" data-testid={`leasing-email-pw-save-${acc.id}`}>Speichern</Button>
                </div>
            )}
        </div>
    );
}

export default function LeasingEmails() {
    const [accounts, setAccounts] = useState([]);
    const [emails, setEmails] = useState([]);
    const [meta, setMeta] = useState({ total: 0, pages: 1 });
    const [filters, setFilters] = useState({ account: "", direction: "", q: "" });
    const [busy, setBusy] = useState(false);

    const loadAccounts = useCallback(() => api.get("/dealer/leasing/email-accounts").then((r) => setAccounts(r.data.items || [])).catch(() => {}), []);
    const loadEmails = useCallback(async (f = filters) => {
        setBusy(true);
        try {
            const params = {};
            if (f.account) params.account = f.account;
            if (f.direction) params.direction = f.direction;
            if (f.q) params.q = f.q;
            const { data } = await api.get("/dealer/leasing/emails", { params });
            setEmails(data.items || []); setMeta({ total: data.total ?? 0, pages: data.pages ?? 1 });
        } catch { /* noop */ }
        finally { setBusy(false); }
    }, [filters]);
    useEffect(() => { loadAccounts(); }, [loadAccounts]);
    useEffect(() => { const t = setTimeout(() => loadEmails(filters), 300); return () => clearTimeout(t); }, [filters, loadEmails]);

    const rematch = async () => {
        try { const { data } = await api.post("/dealer/leasing/emails/rematch"); toast.success(`${data.rematched} E-Mails neu zugeordnet.`); loadEmails(); }
        catch { toast.error("Zuordnung fehlgeschlagen."); }
    };

    return (
        <div className="space-y-8" data-testid="leasing-emails-view">
            <section className="border rounded-sm bg-white">
                <div className="px-4 py-3 border-b flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-indigo-600" /><h3 className="font-black text-sm">Leasing-Postfächer ({accounts.length})</h3></div>
                </div>
                <div className="p-4 space-y-3">
                    {accounts.map((a) => <AccountRow key={a.id} acc={a} onChanged={() => { loadAccounts(); loadEmails(); }} />)}
                    {accounts.length === 0 && <div className="text-sm text-gray-400" data-testid="leasing-email-acct-empty">Noch kein Postfach hinterlegt.</div>}
                    <AddAccount onAdded={loadAccounts} />
                </div>
            </section>

            <section className="border rounded-sm bg-white">
                <div className="px-4 py-3 border-b flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2"><Inbox className="w-4 h-4 text-indigo-600" /><h3 className="font-black text-sm">E-Mail-Verkehr ({meta.total.toLocaleString("de-DE")})</h3></div>
                    <Button onClick={rematch} variant="outline" className="rounded-sm h-8 text-xs" data-testid="leasing-email-rematch"><Link2 className="w-3.5 h-3.5 mr-1" /> Vorgängen neu zuordnen</Button>
                </div>
                <div className="p-4 space-y-3">
                    <div className="flex flex-wrap gap-2 items-center">
                        <input value={filters.q} onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))} placeholder="Suche Betreff / Absender / Inhalt" className="border border-gray-300 rounded-sm px-3 py-1.5 text-sm flex-1 min-w-[220px]" data-testid="leasing-email-search" />
                        <select value={filters.account} onChange={(e) => setFilters((p) => ({ ...p, account: e.target.value }))} className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white" data-testid="leasing-email-filter-account">
                            <option value="">Alle Postfächer</option>
                            {accounts.map((a) => <option key={a.id} value={a.id}>{a.address}</option>)}
                        </select>
                        <select value={filters.direction} onChange={(e) => setFilters((p) => ({ ...p, direction: e.target.value }))} className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white" data-testid="leasing-email-filter-direction">
                            <option value="">Ein- & Ausgang</option>
                            <option value="in">Eingang</option>
                            <option value="out">Ausgang</option>
                        </select>
                    </div>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : emails.length === 0 ? (
                        <div className="text-sm text-gray-400 py-3" data-testid="leasing-email-empty">Keine E-Mails. Postfach verbinden und „Abrufen" klicken.</div>
                    ) : emails.map((m) => (
                        <div key={m.id} className="border rounded-sm p-3" data-testid={`leasing-email-row-${m.id}`}>
                            <div className="flex items-center gap-2 text-sm">
                                {m.direction === "out" ? <Send className="w-3.5 h-3.5 text-blue-500" /> : <Inbox className="w-3.5 h-3.5 text-emerald-600" />}
                                <span className="font-bold truncate">{m.subject || "(kein Betreff)"}</span>
                                {m.matched_return_id
                                    ? <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-indigo-100 text-indigo-800" title={m.matched_by}>zugeordnet</span>
                                    : <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-500">offen</span>}
                                <span className="ml-auto text-[11px] text-gray-400 shrink-0">{m.date ? fmt(m.date) : fmt(m.created_at)}</span>
                            </div>
                            <div className="text-[11px] text-gray-500 mt-0.5">{m.direction === "out" ? `An ${(m.to_addrs || []).join(", ")}` : `Von ${m.from_addr}`}{m.matched_by ? ` · ${m.matched_by}` : ""}</div>
                            {m.body_preview && <div className="text-xs text-gray-600 mt-1 line-clamp-2">{m.body_preview}</div>}
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
